import { Theme } from '@mui/material/styles';

// Consolidated styles object - OPTIMIZED with useMemo to prevent recreation
export const createStyles = (theme: Theme) => ({
  // Main layout
  container: {
    padding: 3
  },
  mainBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 3,
    position: 'relative' // Added for Dialog positioning
  },
  
  // Dialog styles
  dialogTitle: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    padding: 2,
    textAlign: 'center'
  },
  dialogContent: {
    padding: 3,
    textAlign: 'center',
    paddingTop: '24px !important' // Override default MUI padding
  },
  dialogActions: {
    padding: 2,
    justifyContent: 'center'
  },
  dialogButton: {
    paddingInline: '20px',
    minWidth: '100px'
  },
  
  // Loading states
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
    marginTop: '50px'
  },
  loadingText: {
    color: 'text.secondary'
  },
  
  // No unit found
  noUnitContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '50px 20px'
  },
  noUnitTitle: {
    marginBottom: 2
  },
  noUnitText: {
    marginBottom: 1
  },
  
  // Permissions
  permissionsContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '50px'
  },
  
  // Tutorial popover
  tutorialPopover: {
    padding: 3,
    maxWidth: 400
  },
  tutorialHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 2
  },
  tutorialTitle: {
    fontWeight: 600,
    color: theme.palette.primary.main
  },
  tutorialContent: {
    marginBottom: 3,
    lineHeight: 1.6
  },
  tutorialButtons: {
    display: 'flex',
    gap: 1,
    justifyContent: 'flex-end'
  },
  
  // Product information
  productInfoPaper: {
    width: '450px',
    padding: 2,
    backgroundColor: theme.palette.background.paper
  },
  productInfoHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 2
  },
  productTitleSection: {
    flex: 1
  },
  productTitle: {
    fontWeight: 600,
    marginBottom: 1
  },
  productLine: {
    color: 'text.secondary'
  },
  mediaChipsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    alignItems: 'flex-end'
  },
  hotMediaChip: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
    fontSize: '0.75rem',
    height: 24
  },
  coldMediaChip: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontSize: '0.75rem',
    height: 24
  },
  
  // Results section
  resultsPaper: {
    width: '450px',
    padding: 2
  },
  resultsFieldContainer: {
    marginBottom: 2
  },
  resultsLabel: {
    marginBottom: 1
  },
  
  // Loading shimmer
  shimmerPaper: {
    width: '270px',
    height: '172px',
    padding: 2
  },
  shimmerBox: {
    marginBottom: 2
  },
  
  // Output sections
  hotMediaOutputs: {
    border: `2px solid ${theme.palette.error.main}`,
    borderRadius: 1,
    padding: 2,
    marginBottom: 2
  },
  coldMediaOutputs: {
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: 1,
    padding: 2,
    marginBottom: 2
  },
  outputSectionTitle: {
    marginBottom: 2
  },
  outputFieldContainer: {
    marginBottom: 2
  },
  
  // Buttons
  actionButtonsContainer: {
      width: '450px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around'
  },
  calculateButton: {
  //   margin: '10px',
    paddingInline: '20px',
  },
  loadingButtonContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2
  },
  copyButtonContainer: {
    display: 'flex',
    alignItems: 'center',
  //   gap: 1
  },
  copyButton: {
    paddingInline: '20px',
  },
  copyButtonLoading: {
    paddingInline: '20px',
    '& .MuiCircularProgress-root': {
      marginRight: '8px'
    }
  },
  
  // Alerts
  alertContainer: {
    width: '100%',
  },
  warningAlertContainer: {
    width: '100%',
    marginTop: 1
  },
  
  // Help tooltip styles
  helpTooltipContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 1
  },
  helpTooltipText: {
    textAlign: 'center',
    display: 'block',
    marginTop: 1
  },
  
  // Image preview container
  imagePreviewContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 1
  }
});

// Separate CSS styles for regular style attributes
export const cssStyles = {
  hiddenInput: {
    position: 'absolute',
    left: '-9999px',
    opacity: 0,
    pointerEvents: 'none',
    width: '1px',
    height: '1px',
    border: 'none',
    overflow: 'hidden',
    clipPath: 'inset(50%)',
    whiteSpace: 'nowrap'
  },
  helpTooltipContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '8px' // theme.spacing(1) equivalent
  },
  helpTooltipImage: {
    maxWidth: '100%',
    height: 'auto',
    maxHeight: '200px'
  },
  warningImage: {
    width: '300px',
    objectFit: 'contain' as const
  },
  collapsibleStyles: `
    .collapsible {
      cursor: pointer;
      user-select: none;
      position: relative;
      padding-right: 20px;
    }
    .collapsible::after {
      content: '▼';
      position: absolute;
      right: 0;
      transition: transform 0.3s ease;
    }
    .collapsible.collapsed::after {
      transform: rotate(-90deg);
    }
    .collapsedContent {
      max-height: 1000px;
      overflow: hidden;
      transition: max-height 0.3s ease;
    }
    .collapsedContent.collapsed {
      max-height: 0;
    }
  `
}; 